import { inject, Injectable } from '@angular/core';
import { BsApiService, BsHubService } from '@brightside-web/desktop/data-access/core-services';
import { Observable, retry, timer, BehaviorSubject, filter, defaultIfEmpty } from 'rxjs';
import { HubCapsule } from 'aws-amplify/utils';
import { FirebaseService } from '@brightside-web/desktop/data-access/shared';
import { map, take } from 'rxjs/operators';

type EventPayload = { name: string; props?: Record<string, any> };

@Injectable({
  providedIn: 'root'
})
export class ApplicationEventsService {

  private _supportedEvents = new Set<string>();
  private _queue: EventPayload[] = [];
  private _ready$ = new BehaviorSubject<boolean>(false);

  private _bsApi = inject(BsApiService);
  private _bsHub = inject(BsHubService);
  private _firebaseService = inject(FirebaseService);

  constructor() {
    // Receive amplitude events
    this._bsHub.listen('amplitude', (event: HubCapsule<any, any>) => {
      if (event.payload.event === 'event_logged') {
        const name = this._normalize(event.payload.data.eventName);
        const props = event.payload.data.eventProps;
        this._handleIncoming({ name, props });
      }
    });

    // Load supported events (with polling)
    this.getEvents().subscribe({
      next: events => {
        this._supportedEvents = new Set(events.map(e => this._normalize(e)));
        this._ready$.next(true);
        this._drainQueue();
      }
    });
  }

  /** Buffers events until supportedEvents is ready */
  private _handleIncoming(evt: EventPayload) {
    if (!this._ready$.value) {
      this._queue.push(evt);
      return;
    }
    this.postFlowUpdate(evt.name, evt.props);
  }

  /** Processes queued events */
  private _drainQueue() {
    const pending = [...this._queue];
    this._queue.length = 0;

    for (const evt of pending) {
      this.postFlowUpdate(evt.name, evt.props);
    }
  }

  private _normalize(name: string): string {
    return name?.trim().toLowerCase().replace(/\s+/g, '_');
  }

  postFlowUpdate(eventName: string, properties: { [key: string]: any } = {}) {
    if (!this._supportedEvents.has(eventName)) {
      return;
    }

    this._bsApi
      .post('api-mobile', `/application/event`, {
        event: eventName,
        properties: { ...properties },
      })
      .pipe(
        retry({
          count: 3,
          delay: (_error, retryCount) => {
            const backoff = Math.pow(2, retryCount) * 500;
            return timer(backoff);
          },
          resetOnSuccess: true,
        })
      )
      .subscribe({
        next: () => {},
        error: () => {
          /** silent fail as you requested */
        }
      });
  }

  /** Poll until Remote Config returns the events array */
  getEvents(): Observable<string[]> {
    // firebase/remote config isn't always ready to go on the first request
    return timer(250, 500).pipe(
      take(6), // max 6 attempts
      map(() => this._firebaseService.getValueAsArray<string>('applicationEvents')),
      filter((events: string[]) => events.length > 0), // only pass non-empty arrays
      take(1), // take the first non-empty result
      defaultIfEmpty([]) // if all 6 were empty, emit []
    );
  }
}
